import { Injectable } from "@angular/core";
import { Observable, map, throwError } from "rxjs";
import { BaseHandler } from "@app/core/shared/data/network/base-handler";
import { RequestCache } from "@app/core/shared/data/network/request-cache";
import { ApiUrls } from "@app/core/shared/data/network/api-urls";
import {
  HandlerBuilder,
  HttpMethod,
} from "@app/core/shared/data/network/base-handler.parameters";
import { TokenAuthorization } from "@app/core/shared/data/network/token-authorization";
import { TokenRepo } from "@app/core/shared/domain/token.repo";
import { MissingCredentialsError } from "@app/core/shared/domain/missing-credentials.error";
import { CommunityClub } from "@app/core/community/domain/model/community-club";
import {
  CommunityClubsQuery,
  CommunityClubsPage,
} from "@app/core/community/domain/community.repo";
import {
  CommunityClubApiResponse,
  CommunityClubListApiResponse,
  ClubFavoriteResponseApiResponse,
} from "@app/core/shared/domain/api-types-filtered";

function mapClub(data: CommunityClubApiResponse): CommunityClub {
  return new CommunityClub(
    data.id,
    data.name,
    data.city,
    data.country,
    "",
    data.address,
    data.image_url,
    data.cover_image_url,
    0,
    0,
    data.members_count ?? null,
    data.courts_count ?? null,
    data.distance_km,
    false,
    false,
    "",
    null,
    "",
    [],
    (data.courts_aiball_count ?? 0) > 0,
    data.latitude,
    data.longitude,
    data.phone,
    data.website,
    data.google_maps_link,
    [],
    data.all_courts_sacra_active,
    data.is_favorite
  );
}

@Injectable({ providedIn: "root" })
export class CommunityWS {
  constructor(
    private baseHandler: BaseHandler,
    private tokenRepo: TokenRepo
  ) {}

  getClubs(query: CommunityClubsQuery): Observable<CommunityClubsPage> {
    const token = this.tokenRepo.get();
    if (token == null) {
      return throwError(() => new MissingCredentialsError());
    }

    const urlParams: Record<string, string | number> = {};
    if (query.search) urlParams["search"] = query.search;
    if (query.lat != null) {
      urlParams["lat"] = query.lat;
      // Manual location and an active search use an oversized radius so the
      // backend returns every matching club; the frontend then handles the
      // "nearby vs all" filtering itself. A search must never be limited to
      // the nearby radius, or clubs further away silently stop matching.
      urlParams["radius_km"] =
        query.manualLocation || query.search
          ? 1000000
          : query.showAll
            ? 99999
            : 200;
    }
    if (query.lng != null) urlParams["lng"] = query.lng;
    if (query.sortBy) urlParams["sort_by"] = query.sortBy;
    if (query.filter && query.filter !== "ALL")
      urlParams["filter"] = query.filter;
    if (query.page && query.page > 1) urlParams["page"] = query.page;
    if (query.pageSize) urlParams["page_size"] = query.pageSize;
    if (query.onlyFavorites) urlParams["only_favorites"] = "true";

    const builder = new HandlerBuilder()
      .setUrl(ApiUrls.GET_COMMUNITY_CLUBS_ENDPOINT())
      .setMethod(HttpMethod.get)
      .setAuthorization(new TokenAuthorization(token))
      .setCacheTtl(RequestCache.DEFAULT_TTL_MS);
    if (Object.keys(urlParams).length > 0) {
      builder.setUrlParams(urlParams);
    }
    return this.baseHandler.execute(builder.build()).pipe(
      map((response: CommunityClubListApiResponse) => ({
        clubs: response.results.map(mapClub),
        total: response.total,
      }))
    );
  }

  getClubById(id: string): Observable<CommunityClub> {
    const token = this.tokenRepo.get();
    if (token == null) {
      return throwError(() => new MissingCredentialsError());
    }
    const parameters = new HandlerBuilder()
      .setUrl(ApiUrls.GET_COMMUNITY_CLUB_DETAIL_ENDPOINT(id))
      .setMethod(HttpMethod.get)
      .setAuthorization(new TokenAuthorization(token))
      .build();
    return this.baseHandler
      .execute(parameters)
      .pipe(map((data: CommunityClubApiResponse) => mapClub(data)));
  }

  toggleFavorite(id: string, isFavorite: boolean): Observable<boolean> {
    const token = this.tokenRepo.get();
    if (token == null) {
      return throwError(() => new MissingCredentialsError());
    }
    const parameters = new HandlerBuilder()
      .setUrl(ApiUrls.TOGGLE_COMMUNITY_CLUB_FAVORITE_ENDPOINT(id))
      .setMethod(HttpMethod.post)
      .setAuthorization(new TokenAuthorization(token))
      .setBody({ is_favorite: isFavorite })
      .build();
    return this.baseHandler
      .execute(parameters)
      .pipe(map((data: ClubFavoriteResponseApiResponse) => data.is_favorite));
  }
}
